import { ChangeDetectorRef, Component, Renderer2 } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { getInstanceRegion } from '../../../global.variable';
import { AppService } from '../../app.service';
import { UserProfileService } from '../../profile/user-profile/user-profile.service';
import { DeviceInfoService } from '../../shared/services/device-info.service';
import { LoadingSpinnerService } from '../../shared/services/loading-spinner.service';
import { MustMatch } from '../../shared/validators/must-match-validator';
import { AuthService } from '../auth.service';
@Component({
  selector: 'gtapp-register',
  templateUrl: './register.component.html',
  styleUrl: './register.component.scss',
  standalone: false,
})
export class RegisterComponent {
  currentTheme: string = 'default';

  registerForm: UntypedFormGroup = new UntypedFormGroup(
    {
      company_name: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(4),
      ]),
      addressLookup: new UntypedFormControl(''),
      address1: new UntypedFormControl(''),
      address2: new UntypedFormControl('', [Validators.required]),
      address3: new UntypedFormControl(''),
      city_name: new UntypedFormControl('', [Validators.required]),
      postcode: new UntypedFormControl(''),
      longitude: new UntypedFormControl('', [Validators.required]),
      latitude: new UntypedFormControl('', [Validators.required]),
      country_name: new UntypedFormControl(getInstanceRegion(), [
        Validators.required,
      ]),
      email: new UntypedFormControl('', [
        Validators.required,
        Validators.email,
      ]),
      vCode: new UntypedFormControl(''),
      state_code: new UntypedFormControl('', [Validators.required]),
      state_name: new UntypedFormControl('', [Validators.required]),
      first_name: new UntypedFormControl('', [Validators.required]),
      last_name: new UntypedFormControl('', [Validators.required]),
      password: new UntypedFormControl(null, [
        Validators.required,
        Validators.minLength(8),
      ]),
      confirm_password: new UntypedFormControl(null, [
        MustMatch('password', 'confirm_password'),
      ]),
      mobile_number: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(7),
        Validators.maxLength(10),
        //Validators.pattern('^((\\+91-?)|0)?[0-9]{10}$'),
        Validators.pattern('^(\\+?(61|64|675)|0)?[0-9]{7,10}$'),
      ]),
    },
    {
      validators: MustMatch('password', 'confirm_password'),
    },
  );

  submitted: any;

  error: any;

  alreadyRegistered: boolean = false;

  gotVerificationCode: boolean = false;
  verificationToken: any;

  showPassword: boolean = false;
  currentStep: number = 1;
  gtmLoaded: boolean = false;
  constructor(
    private spinnerService: LoadingSpinnerService,
    private cd: ChangeDetectorRef,
    private appService: AppService,
    private authService: AuthService,
    public router: Router,
    private deviceInfoService: DeviceInfoService,
    private profileService: UserProfileService,
    private renderer: Renderer2,
  ) {}
  isVerificationTokenValid(typedInEmail: any) {
    if (!localStorage.getItem('verificationToken')) return false;
    if (typedInEmail !== localStorage.getItem('verificationEmail'))
      return false;
    let currentTime = new Date().getTime();
    let expiryTokenTime: any = localStorage.getItem('verificationExpiry');
    if (!expiryTokenTime) return false;
    return currentTime < JSON.parse(expiryTokenTime);
  }

  async ngOnInit(): Promise<void> {
    this.appService.checkServerStatus().subscribe((response: any) => {
      // save server region on localStorage for changing ui params. Used from global.varaible.ts
      localStorage.setItem(
        btoa('INSTANCE_REGION_NAME'),
        response?.INSTANCE_REGION_NAME || 'AUSTRALIA',
      );
      this.registerForm.controls['country_name'].setValue(
        response?.INSTANCE_REGION_NAME || 'AUSTRALIA',
      );
    });
    document.documentElement.setAttribute('data-bs-theme', 'light');

    // Call the get_IP API if IP address is not capture of user
    if (!localStorage.getItem('ipAddress')) {
      this.deviceInfoService.setIp();
    }
    this.spinnerService.show();
    // Check and Fetch the register details if it exists
    let registerDetails: any = localStorage.getItem('registerDetails');
    registerDetails = JSON.parse(registerDetails);
    if (registerDetails?.expiry) {
      if (new Date().getTime() > registerDetails?.expiry) {
        localStorage.removeItem('registerDetails');
      } else {
        this.registerForm.patchValue(registerDetails?.values);

        if (
          localStorage.getItem('verificationToken') &&
          this.isVerificationTokenValid(
            this.registerForm.controls['email'].value,
          )
        ) {
          this.gotVerificationCode = true;
        }

        if (this.registerForm.valid) {
          this.currentStep = 5;
        } else {
          this.currentStep = 0;
          setTimeout(() => {
            this.currentStep = 1;
          }, 50);
        }
      }
    }

    this.spinnerService.hide();
    const swUpdate: any = this.appService.getSWUpdate();

    if (swUpdate?.isEnabled) {
      try {
        if (await swUpdate?.checkForUpdate()) {
          const updateApp = await swUpdate?.activateUpdate().then(() => {
            sessionStorage.removeItem('updateAvailable');
            window.location.reload();
          });
        } else {
          this.appService.appVersionCheck();
        }
      } catch (error) {
        throw error;
      }
    }

    setTimeout(() => {
      if (sessionStorage.getItem('updateAvailable') === 'true') {
        sessionStorage.removeItem('updateAvailable');
        window.location.reload();
      }
    }, 2000);
    if (environment.production === true) {
      // run only in prod & staging environment
      this.loadTrackerScripts();
    }
  }

  addressSelected(address: any) {
    setTimeout(() => {
      window.scrollTo(0, document.body.scrollHeight);
    }, 100);
  }

  sendVerificationCode() {
    const isVerificationCodeValid = this.isVerificationTokenValid(
      this.registerForm.controls['email'].value,
    );
    if (isVerificationCodeValid) return;
    this.authService
      .sendVerificationCode(
        this.registerForm.controls['email'].value,
        this.registerForm.controls['first_name'].value,
        this.registerForm.controls['last_name'].value,
      )
      .subscribe((response: any) => {
        if (response['status'] == 'success') {
          this.gotVerificationCode = true;
          this.verificationToken = response['token'];
          //Save the token and expiration time which will expire in 10 minutes
          let verificationExpiry = new Date().getTime() + 10 * 60 * 1000;
          localStorage.setItem(
            'verificationExpiry',
            JSON.stringify(verificationExpiry),
          );
          localStorage.setItem('verificationToken', this.verificationToken);
          localStorage.setItem(
            'verificationEmail',
            this.registerForm.controls['email'].value,
          );
        } else {
          this.error = response['message'];
          if (response?.sign_in && this.registerForm.controls['email'].value) {
            this.alreadyRegistered = true;
          }
        }
      });
  }
  // Method to verify the verification code sent in user's email
  verifyVerificationCode(event: any) {
    if (event?.target?.value?.length >= 4) {
      this.spinnerService.show();
      this.verificationToken = localStorage.getItem('verificationToken')
        ? localStorage.getItem('verificationToken')
        : this.verificationToken;
      const body = { v_code: this.registerForm.controls['vCode'].value };
      this.appService
        .gtExternalLink(
          'external_api/verify_v_code',
          body,
          this.verificationToken,
          false,
        )
        .then((response: any) => {
          if (response['status'] == 'success') {
            this.verificationToken = response['token'];
            localStorage.setItem('verificationToken', this.verificationToken);
            this.error = '';
            this.finalRegister();
          } else {
            this.error = response['message'];

            if (
              response?.sign_in &&
              this.registerForm.controls['email'].value
            ) {
              this.alreadyRegistered = true;
            }
            this.spinnerService.hide();
            if (response['restart'] === true) {
              this.gotVerificationCode = false;
              localStorage.removeItem('registerDetails');
              localStorage.removeItem('verificationToken');
              localStorage.removeItem('verificationExpiry');
              localStorage.removeItem('verificationEmail');
              this.error = 'We could not verify your account';
            }
          }
        });
    }
  }

  //Method to register the user after verifying email
  async finalRegister(): Promise<void> {
    if (this.registerForm.valid) {
      this.spinnerService.show('Setting up Guard Tracker');
      this.submitted = true;
      const loggedInDeviceID: string =
        this.deviceInfoService.getUniqueDeviceID();

      const body = {
        ...this.registerForm.value,
        unique_device_id: loggedInDeviceID,
        device_info: await this.deviceInfoService.getDeviceInfo(),
      };
      this.appService
        .gtExternalLink(
          'register_login/register',
          body,
          this.verificationToken,
          false,
        )
        .then((response: any) => {
          this.submitted = false;
          if (response) {
            if (response['status'] == 'success') {
              this.spinnerService.show('Setting up Guard Tracker');
              localStorage.removeItem('registerDetails');
              this.appService.setUserData(response);
              if (environment.production === true) {
                // run only in prod & staging environment
                this.loadTrackerScripts();
              }
              setTimeout(async () => {
                await this.getUserConfig();
              }, 200);

              localStorage.removeItem('verificationToken');
              localStorage.removeItem('verificationExpiry');
              localStorage.removeItem('verificationEmail');
            } else {
              this.error = response['message'];
              this.spinnerService.hide();
            }
          }
          this.cd.detectChanges();
        });
    }
  }
  async getUserConfig() {
    this.spinnerService.show();

    this.profileService
      .getUserConfig({
        remember_login: 1,
        time_offset: new Date().getTimezoneOffset(),
      })
      .subscribe((resp: any) => {
        if (resp['status'] == 'success') {
          let permissionToken = resp?.user_permission_token;
          let userData = this.appService.getUserData();
          if (permissionToken) {
            userData.user_token = permissionToken;
          }
          userData.user_token = resp?.user_permission_token;
          this.appService.setUserData(userData);
          localStorage.removeItem('firstTimeSubscriber');
          setTimeout(() => {
            this.router.navigate(['/']);
          }, 100);
        }
      });
  }

  // Set the Registration details in local-storage
  saveRegisterDetail() {
    const item = {
      values: this.registerForm.value,
      expiry: new Date().getTime() + 2 * 60 * 60 * 1000,
    };
    localStorage.setItem('registerDetails', JSON.stringify(item));
  }

  refresh(): void {
    localStorage.clear();
    window.location.reload();
  }

  goToStep(stepCount: number) {
    this.currentStep = stepCount;
  }
  onNext() {
    if (
      this.registerForm.controls['password'].valid &&
      this.registerForm.controls['confirm_password'].valid
    ) {
      this.goToStep(5);
    }
  }
  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }
  goToLogin() {
    if (this.registerForm.controls['email'].value) {
      localStorage.setItem('email', this.registerForm.controls['email'].value);
    }
    this.router.navigate(['/login']);
  }

  loadTrackerScripts() {
    if (this.gtmLoaded || (window as any).__gtmLoaded) return;

    // Initialize dataLayer
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      'gtm.start': new Date().getTime(),
      'event': 'gtm.js',
    });

    const script = this.renderer.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-KXQ6JXRV';

    this.renderer.appendChild(document.head, script);

    this.gtmLoaded = true;
    (window as any).__gtmLoaded = true;
  }
}
